/**
 * useStatusPageData.ts — Hook for nself-status-page plugin (port 3832)
 *
 * Purpose:    Fetch and manage state for the Status Page dashboard.
 * Inputs:     None (reads from plugin backend port 3832)
 * Outputs:    { state, data, error, refresh, updateComponentStatus, addSubscriber }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3832

export type ComponentStatus = 'operational' | 'degraded' | 'partial_outage' | 'major_outage' | 'maintenance'

export interface StatusComponent {
  id: string
  name: string
  status: ComponentStatus
  order: number
}

export interface StatusIncident {
  id: string
  title: string
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  severity: 'minor' | 'major' | 'critical'
  createdAt: string
  resolvedAt?: string
  updates: Array<{ message: string; createdAt: string }>
}

export interface StatusSubscriber {
  id: string
  email: string
  subscribedAt: string
}

export interface StatusPageData {
  publicUrl: string
  components: StatusComponent[]
  incidents: StatusIncident[]
  subscribers: StatusSubscriber[]
}

export function useStatusPageData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<StatusPageData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) {
      setState('offline')
      return
    }
    const result = await pluginFetch<StatusPageData>(PORT, '/api/status-page/summary')
    if (result.ok) {
      setData(result.value)
      setState('success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const updateComponentStatus = useCallback(
    async (id: string, status: ComponentStatus): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/status-page/components/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const addSubscriber = useCallback(
    async (email: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, '/api/status-page/subscribers', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  return { state, data, error, refresh: load, updateComponentStatus, addSubscriber }
}
